// src/pages/patient/AIAssistantChat.jsx
//
// /patient/ai-assistant -- AVIA, the authenticated booking assistant
// (Patient Appointments Task 4). Rendered under AppShell, which redirects to
// sign-in before anything personal loads.
//
// Behaviour:
//   * fixed-height layout: header and composer stay visible; the message list
//     and the conversation list scroll independently; mobile toggles the list;
//   * auto-scroll only when the patient is already near the latest message;
//   * one request at a time (duplicate sends ignored), Cancel aborts it, a
//     failed message can be retried;
//   * responses for a conversation the patient has left are discarded;
//   * the active time zone is always shown and can be changed explicitly;
//   * holding, booking, cancelling and rescheduling go through application
//     dialogs, and only through the server's own buttons (opaque refs);
//   * payment uses the same checkout endpoint as the booking page, once.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { apiErrorCode, readApiError } from "../../lib/apiErrors";
import { browserTimezone, formatDateTimeInZone, isValidTimezone } from "../../lib/displayTimezone";
import { patientAIAssistantApi } from "../../services/patientAIAssistant";
import { appointmentWorkspaceApi } from "../../services/patientAppointmentWorkspace";
import { isAbortError } from "../../lib/useSequencedRequest";
import useSpeechInput from "../../lib/useSpeechInput";
import AssistantCard from "../../components/patient/assistant/AssistantCards";
import { AssistantAvatar, FormattedReply, MessageRow, WorkingRow } from "../../components/patient/assistant/ChatMessage";
import ConversationSidebar, { mergeConversations } from "../../components/patient/assistant/ConversationSidebar";
import { FilterBar, FilterEditor } from "../../components/patient/assistant/AssistantFilters";
import { patientDiscoveryApi } from "../../services/patientDiscovery";
import Dialog from "../../components/patient/appointments/Dialog";

export const NEAR_BOTTOM_PX = 120;

export const STARTERS = ["Find a physiotherapist", "Book a video appointment", "Show my upcoming appointments"];

// What the patient is waiting for -- never an indefinite generic "typing".
export const ACTION_STATUS = {
  select_slot: "Checking that time…",
  confirm_hold: "Reserving your time…",
  confirm_booking: "Confirming your booking…",
  release_hold: "Releasing the held time…",
  prepare_cancel: "Checking cancellation options…",
  confirm_cancel: "Cancelling your appointment…",
  prepare_reschedule: "Finding new times…",
  confirm_reschedule_hold: "Reserving the new time…",
  confirm_reschedule: "Moving your appointment…",
  list_appointments: "Loading your appointments…",
  show_slots: "Searching for available times…",
};

const COMMON_ZONES = ["UTC", "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Toronto", "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Singapore", "Australia/Sydney"];

let localId = 0;
function nextId() {
  localId += 1;
  return `local-${localId}`;
}

function describeAction(button, card) {
  const when = card?.start_at ? formatDateTimeInZone(card.start_at, card.timezone) : "";
  switch (button.action) {
    case "confirm_hold":
      return { title: "Hold this time?", body: `${card?.service_name || "This appointment"}${when ? ` on ${when}` : ""}. ${card?.payment_message || ""} The time is held for a few minutes while you confirm.` };
    case "confirm_booking":
      return { title: card?.payment_requirement === "required" ? "Book and continue to payment?" : "Confirm this booking?", body: `${card?.service_name || "Appointment"}${when ? ` on ${when}` : ""}. ${card?.payment_message || ""}` };
    case "confirm_cancel":
      return { title: "Cancel this appointment?", body: `${card?.service_name || "Appointment"}${when ? ` on ${when}` : ""}. This cannot be undone.` };
    case "confirm_reschedule_hold":
      return { title: "Hold this new time?", body: `${when}. Your current appointment is unchanged until you confirm.` };
    case "confirm_reschedule":
      return { title: "Move your appointment?", body: `Your appointment will move to ${when}.` };
    default:
      return { title: "Continue?", body: "" };
  }
}

export default function AIAssistantChat() {
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [sessionsError, setSessionsError] = useState("");
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsHasMore, setSessionsHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterState, setFilterState] = useState(null);
  const [editingFilters, setEditingFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filterOptionsError, setFilterOptionsError] = useState("");
  const [locating, setLocating] = useState(false);
  const sessionsSeqRef = useRef(0);
  const sessionsPageRef = useRef(1);
  const listToggleRef = useRef(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => (typeof location.state?.draft === "string" ? location.state.draft.slice(0, 2000) : ""));
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(null); // { text } or { action }
  const [error, setError] = useState("");
  const [zone, setZone] = useState({ name: browserTimezone() || "UTC", source: "browser" });
  const [selectedZone, setSelectedZone] = useState("");
  const [confirming, setConfirming] = useState(null); // { button, card }
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [payingLink, setPayingLink] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [showJump, setShowJump] = useState(false);
  const [status, setStatus] = useState("");
  const [voiceReview, setVoiceReview] = useState(false);
  const inputRef = useRef(null);
  const voice = useSpeechInput({
    onTranscript: (text) => {
      // Shown for correction; never sent automatically.
      setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text).slice(0, 2000));
      setVoiceReview(true);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    },
  });
  const voiceEntry = Boolean(location.state?.voice);

  const scrollRef = useRef(null);
  const nearBottomRef = useRef(true);
  const controllerRef = useRef(null);
  const sendingRef = useRef(false);
  const conversationRef = useRef(0);
  const payingRef = useRef(false);

  // History refreshes never touch the open conversation. A late response
  // (sequence number) never overwrites a newer list; page 1 is MERGED into
  // what is loaded so pages stay and nothing is duplicated.
  const loadSessions = useCallback(async () => {
    const seq = ++sessionsSeqRef.current;
    try {
      const data = await patientAIAssistantApi.sessions({ page: 1 });
      if (seq !== sessionsSeqRef.current) return;
      setSessions((prev) => mergeConversations(prev, data.items || []));
      setSessionsHasMore((prevMore) => (sessionsPageRef.current > 1 ? prevMore : Boolean(data.has_more)));
      setSessionsError("");
    } catch (err) {
      if (seq === sessionsSeqRef.current) setSessionsError(readApiError(err, "Conversations could not be loaded."));
    } finally {
      if (seq === sessionsSeqRef.current) setSessionsLoading(false);
    }
  }, []);

  async function loadMoreSessions() {
    if (loadingMore) return;
    setLoadingMore(true);
    const next = sessionsPage + 1;
    try {
      const data = await patientAIAssistantApi.sessions({ page: next });
      setSessions((prev) => mergeConversations(prev, data.items || []));
      setSessionsPage(next);
      sessionsPageRef.current = next;
      setSessionsHasMore(Boolean(data.has_more));
    } catch (err) {
      setSessionsError(readApiError(err, "More conversations could not be loaded."));
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    loadSessions();
    return () => controllerRef.current?.abort();
  }, [loadSessions]);

  // Auto-scroll only if the patient was already near the bottom.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    if (nearBottomRef.current) {
      node.scrollTop = node.scrollHeight;
      setShowJump(false);
    } else if (messages.length) {
      setShowJump(true);
    }
  }, [messages, sending]);

  function onScroll() {
    const node = scrollRef.current;
    if (!node) return;
    nearBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight <= NEAR_BOTTOM_PX;
    if (nearBottomRef.current) setShowJump(false);
  }

  function jumpToLatest() {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
    nearBottomRef.current = true;
    setShowJump(false);
  }

  function applyTurn(body, ticket) {
    if (ticket !== conversationRef.current) return false; // the patient moved to another conversation
    setSessionId(body.session_id);
    if (body.timezone?.name) setZone(body.timezone);
    if (body.filters) setFilterState(body.filters);
    // The server's title and server clock -- never a client-made timestamp.
    if (body.title && body.server_now) {
      setSessions((prev) => {
        const existing = prev.find((item) => item.id === body.session_id);
        return mergeConversations(prev, [{ id: body.session_id, title: body.title, updated_at: body.server_now, created_at: existing?.created_at || body.server_now }]);
      });
    }
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: "assistant",
        createdAt: new Date().toISOString(),
        content: body.reply,
        cards: body.cards || [],
        suggestions: body.suggestions || [],
        errorCode: body.error_code || null,
      },
    ]);
    return true;
  }

  async function run(request, { userText, retry, statusText }) {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setStatus(statusText || "AVIA is working…");
    const ticket = conversationRef.current;
    const controller = typeof AbortController === "undefined" ? null : new AbortController();
    controllerRef.current = controller;
    setSending(true);
    setError("");
    setFailed(null);
    if (userText) {
      nearBottomRef.current = true;
      setMessages((prev) => [...prev, { id: nextId(), role: "user", content: userText, createdAt: new Date().toISOString() }]);
    }
    try {
      const body = await request({ signal: controller?.signal });
      if (applyTurn(body, ticket)) loadSessions();
    } catch (err) {
      if (ticket !== conversationRef.current) return;
      if (isAbortError(err)) {
        // Stopping the wait does not undo work the server may already have done.
        setError(retry?.button
          ? "Stopped waiting. If this already reached the server it may have completed -- check your appointments before trying again."
          : "Request cancelled.");
        setFailed(retry);
      } else if (apiErrorCode(err) === "CONVERSATION_NOT_FOUND") {
        setError("That conversation is no longer available.");
      } else {
        setError(readApiError(err, "AVIA couldn't respond. Please try again."));
        setFailed(retry);
      }
      // Keep what the patient typed: put it back unless they already typed again.
      if (retry?.text) setInput((prev) => prev || retry.text);
    } finally {
      if (ticket === conversationRef.current) setSending(false);
      sendingRef.current = false;
      controllerRef.current = null;
    }
  }

  function send(text) {
    const message = String(text ?? input).trim();
    if (!message || sendingRef.current) return;
    if (voice.state === "listening") voice.cancel();
    setInput("");
    setVoiceReview(false);
    const payload = { sessionId, message, timezone: selectedZone || undefined, browserTimezone: browserTimezone() };
    run((opts) => patientAIAssistantApi.send(payload, opts), { userText: message, retry: { text: message }, statusText: "Searching…" });
  }

  function act(button, extra = {}) {
    if (!sessionId || sendingRef.current) return;
    const payload = { sessionId, action: button.action, ref: button.ref, timezone: selectedZone || undefined, browserTimezone: browserTimezone(), ...extra };
    run((opts) => patientAIAssistantApi.act(payload, opts), { userText: button.label, retry: { button, extra }, statusText: ACTION_STATUS[button.action] });
  }

  function retryFailed() {
    if (!failed) return;
    if (failed.text) {
      // Remove the failed user bubble so the retry does not duplicate it.
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last?.role === "user" && last.content === failed.text) copy.pop();
        return copy;
      });
      send(failed.text);
    } else if (failed.button) {
      act(failed.button, failed.extra);
    } else if (failed.filters) {
      applyFilters(failed.filters);
    }
  }

  // Chips, Edit filters and "use my location" update the same server state as chat.
  function applyFilters(change) {
    if (sendingRef.current) return;
    const payload = { sessionId, timezone: selectedZone || undefined, browserTimezone: browserTimezone(), ...change };
    const label = change.near ? "Use my location" : change.reset ? "Start a new search" : "Update filters";
    run((opts) => patientAIAssistantApi.filters(payload, opts), { userText: label, retry: { filters: change }, statusText: "Updating your search…" });
  }

  async function openFilterEditor() {
    setEditingFilters(true);
    if (filterOptions) return;
    try {
      setFilterOptions(await patientDiscoveryApi.filterOptions({}));
      setFilterOptionsError("");
    } catch (err) {
      setFilterOptionsError(readApiError(err, "Some choices couldn't be loaded. You can still type your request."));
    }
  }

  // Location permission is asked only when the patient chooses it; the
  // coordinates go to the server once to find the nearest bookable city.
  function shareMyLocation() {
    if (locating) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setError("Location isn't available in this browser. Choose a city instead.");
      openFilterEditor();
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        setEditingFilters(false);
        applyFilters({ near: { latitude: position.coords.latitude, longitude: position.coords.longitude } });
      },
      () => {
        setLocating(false);
        setError("Location permission was denied. Choose a city instead.");
        openFilterEditor();
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  }

  function cancelRequest() {
    controllerRef.current?.abort();
  }

  function onAction(button, card) {
    if (sendingRef.current) return;
    if (button.requires_confirmation) {
      setReason("");
      setReasonError("");
      setConfirming({ button, card });
      return;
    }
    act(button);
  }

  function confirmPending() {
    if (!confirming) return;
    const { button } = confirming;
    const trimmed = reason.trim();
    if (button.reason === "required" && !trimmed) {
      setReasonError("Please tell us why you are cancelling.");
      return;
    }
    setConfirming(null);
    act(button, button.reason !== "none" ? { reason: trimmed } : {});
  }

  async function pay(card) {
    if (payingRef.current || !card.payment_appointment_link) return;
    payingRef.current = true;
    setPayingLink(card.payment_appointment_link);
    try {
      const appointmentId = card.payment_appointment_link.split("/").pop();
      const checkout = await appointmentWorkspaceApi.startCheckout(appointmentId);
      if (!checkout?.checkout_url) throw new Error("missing checkout url");
      window.location.assign(checkout.checkout_url);
    } catch (err) {
      setError(readApiError(err, "Payment could not be started. Your appointment is still reserved -- open it to pay."));
      payingRef.current = false;
      setPayingLink("");
    }
  }

  function startNew() {
    conversationRef.current += 1;
    controllerRef.current?.abort();
    sendingRef.current = false;
    setSending(false);
    setSessionId(null);
    setMessages([]);
    setFilterState(null);
    setFailed(null);
    setError("");
    setListOpen(false);
  }

  async function openSession(id) {
    conversationRef.current += 1;
    const ticket = conversationRef.current;
    controllerRef.current?.abort();
    sendingRef.current = false;
    setSending(false);
    setFailed(null);
    setError("");
    setListOpen(false);
    try {
      const data = await patientAIAssistantApi.session(id);
      if (ticket !== conversationRef.current) return;
      setSessionId(data.id);
      setFilterState(data.filters || null);
      if (data.selected_timezone) setSelectedZone(data.selected_timezone);
      nearBottomRef.current = true;
      // Restored history is display-only: its buttons are rendered disabled
      // (only the latest live turn is actionable) and nothing is replayed.
      setMessages((data.messages || []).map((m) => ({ id: m.id, role: m.role, content: m.content, cards: m.cards || [], suggestions: [], createdAt: m.created_at, restored: true })));
    } catch (err) {
      if (ticket === conversationRef.current) setError(readApiError(err, "That conversation could not be opened."));
    }
  }

  function changeZone(value) {
    if (value && !isValidTimezone(value)) return;
    setSelectedZone(value);
    if (value && sessionId && !sendingRef.current) {
      const payload = { sessionId, message: `Show times in ${value}`, timezone: value, browserTimezone: browserTimezone() };
      run((opts) => patientAIAssistantApi.send(payload, opts), { userText: `Show times in ${value}`, retry: { text: `Show times in ${value}` } });
    }
  }

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const zoneOptions = Array.from(new Set([browserTimezone(), zone.name, ...COMMON_ZONES].filter(Boolean)));

  return (
    <div className="mx-auto flex h-[calc(100dvh-9rem)] min-h-[520px] w-full max-w-6xl gap-4 px-2 py-2 sm:px-4" data-testid="assistant-layout">
      <ConversationSidebar
        open={listOpen}
        onClose={() => setListOpen(false)}
        toggleRef={listToggleRef}
        sessions={sessions}
        zone={zone.name}
        activeId={sessionId}
        loading={sessionsLoading}
        error={sessionsError}
        hasMore={sessionsHasMore}
        loadingMore={loadingMore}
        onRetry={loadSessions}
        onLoadMore={loadMoreSessions}
        onSelect={openSession}
        onNew={startNew}
      />

      <section className="flex min-h-0 min-w-0 flex-1 flex-col rounded-3xl border border-slate-200 bg-white" aria-label="AVIA conversation">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <button ref={listToggleRef} type="button" className="rounded-xl border border-slate-300 px-2 py-1 text-sm md:hidden" onClick={() => setListOpen((v) => !v)} aria-expanded={listOpen}>
              Conversations
            </button>
            <h1 className="text-base font-semibold">AVIA</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600" data-testid="assistant-timezone">Times in {zone.name}</span>
            <label htmlFor="assistant-zone" className="sr-only">Time zone</label>
            <select id="assistant-zone" value={selectedZone} onChange={(e) => changeZone(e.target.value)} disabled={sending} className="rounded-xl border border-slate-300 px-2 py-1 text-xs">
              <option value="">Use my device time zone</option>
              {zoneOptions.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
        </header>
        <FilterBar
          filters={filterState}
          currency={filterOptions?.currencies?.length === 1 ? filterOptions.currencies[0] : ""}
          disabled={sending}
          onRemove={(clearKey) => applyFilters({ clear: [clearKey] })}
          onEdit={openFilterEditor}
        />

        <div ref={scrollRef} onScroll={onScroll} className="relative min-h-0 flex-1 overflow-y-auto bg-slate-50/60 px-3 py-5 sm:px-6" data-testid="message-list" role="log" aria-live="polite" aria-relevant="additions">
          {!messages.length ? (
            <div className="mx-auto flex max-w-lg flex-col items-center py-10 text-center text-sm text-slate-600">
              <AssistantAvatar className="h-14 w-14 text-xl" />
              <p className="mt-4 text-lg font-semibold text-slate-900">Hi, I'm AVIA</p>
              <p className="mt-1 leading-relaxed">Tell me what care you need, when and how — for example <span className="rounded-md bg-violet-50 px-1.5 py-0.5 font-medium text-violet-800">“a dermatologist tomorrow evening by video”</span>. I only show real available times, and nothing is booked until you confirm.</p>
              <p className="mt-3 text-xs text-slate-500">AVIA helps you find and book appointments. It can't diagnose or give medical advice; in an emergency call your local emergency number.</p>
              {voiceEntry && voice.supported ? <p className="mt-2 text-xs font-semibold text-sky-700" data-testid="voice-entry-hint">Tap the microphone below to speak your request.</p> : null}
              <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="starter-suggestions">
                {STARTERS.map((starter) => (
                  <button key={starter} type="button" onClick={() => send(starter)} disabled={sending}
                    className="rounded-full border border-violet-200 bg-white px-3.5 py-2 text-xs font-semibold text-violet-700 shadow-sm transition hover:bg-violet-50 disabled:opacity-50">
                    {starter}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <ol className="space-y-6">
            {messages.map((m) => {
              const live = m === lastAssistant;
              const extras = m.role === "assistant" && ((m.cards || []).length || (live && m.suggestions?.length)) ? (
                <>
                  {(m.cards || []).map((card, index) => (
                    <AssistantCard
                      key={`${m.id}-${card.ref || index}`}
                      card={card}
                      disabled={sending || !live}
                      onAction={onAction}
                      onPay={pay}
                      payingLink={payingLink}
                      onChoice={live ? send : undefined}
                      onUseLocation={live ? shareMyLocation : undefined}
                      onEditFilters={live ? openFilterEditor : undefined}
                      locating={locating}
                    />
                  ))}
                  {live && m.suggestions?.length ? (
                    <div className="flex flex-wrap gap-2" aria-label="Suggestions">
                      {m.suggestions.map((suggestion) => (
                        <button key={suggestion} type="button" onClick={() => send(suggestion)} disabled={sending}
                          className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 transition hover:bg-violet-100 disabled:opacity-50">
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null;
              return (
                <li key={m.id}>
                  <MessageRow role={m.role} createdAt={m.createdAt} extras={extras}>
                    {m.role === "assistant" ? <FormattedReply text={m.content} /> : m.content}
                  </MessageRow>
                </li>
              );
            })}
          </ol>
          {sending ? <div className="mt-6"><WorkingRow status={status} /></div> : null}
        </div>
        {showJump ? (
          <button type="button" onClick={jumpToLatest} className="mx-auto -mt-12 mb-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white" data-testid="jump-latest">
            New messages
          </button>
        ) : null}

        {error ? (
          <div role="alert" className="mx-4 mb-2 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            <span>{error}</span>
            {failed ? <button type="button" onClick={retryFailed} className="rounded-xl border border-rose-300 bg-white px-2 py-1 text-xs font-semibold">Retry</button> : null}
          </div>
        ) : null}

        {voice.state === "listening" ? (
          <div role="status" className="mx-4 mb-2 flex items-center justify-between gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800" data-testid="voice-listening">
            <span>Listening…{voice.interim ? ` "${voice.interim}"` : ""}</span>
            <button type="button" onClick={voice.cancel} className="rounded-xl border border-sky-300 bg-white px-2 py-1 text-xs font-semibold">Discard</button>
          </div>
        ) : null}
        {voice.error ? (
          <div role="alert" className="mx-4 mb-2 flex items-center justify-between gap-2 rounded-2xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="voice-error">
            <span>{voice.error}</span>
            <button type="button" onClick={voice.dismissError} className="rounded-xl border border-amber-300 bg-white px-2 py-1 text-xs font-semibold">OK</button>
          </div>
        ) : null}
        {voiceReview && input.trim() ? (
          <p className="mx-4 mb-1 text-xs text-slate-600" data-testid="voice-review">Check the transcript below, correct it if needed, then press Send.</p>
        ) : null}

        <form className="flex items-end gap-2 border-t border-slate-100 bg-white px-3 py-3 sm:px-4" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <label htmlFor="assistant-input" className="sr-only">Message AVIA</label>
          <textarea
            ref={inputRef}
            id="assistant-input"
            rows={1}
            value={input}
            maxLength={2000}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask AVIA to find a time…"
            className="max-h-32 min-h-[48px] flex-1 resize-none rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-[15px] leading-relaxed outline-none transition focus:border-violet-300 focus:bg-white focus:ring-2 focus:ring-violet-200"
          />
          <button
            type="button"
            onClick={() => (voice.state === "listening" ? voice.stop() : voice.start())}
            disabled={sending}
            aria-pressed={voice.state === "listening"}
            aria-label={voice.state === "listening" ? "Stop voice input" : "Start voice input"}
            title={voice.supported ? "Speak your request" : "Voice input isn't supported in this browser"}
            className={`flex min-h-[48px] items-center rounded-2xl border px-3 text-sm font-semibold transition disabled:opacity-50 ${voice.state === "listening" ? "border-violet-400 bg-violet-50 text-violet-700 ring-2 ring-violet-200" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid="voice-button"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          {sending ? (
            <button type="button" onClick={cancelRequest} className="rounded-2xl border border-slate-300 px-4 py-2 text-sm font-semibold">Cancel</button>
          ) : (
            <button type="submit" disabled={!input.trim()} className="min-h-[48px] rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-500 px-5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 disabled:opacity-40">Send</button>
          )}
        </form>
      </section>

      <Dialog open={editingFilters} title="Edit filters" labelId="assistant-filters-title" onClose={() => setEditingFilters(false)}>
        {editingFilters ? (
          <FilterEditor
            filters={filterState}
            options={filterOptions}
            optionsError={filterOptionsError}
            locating={locating}
            onUseLocation={shareMyLocation}
            onCancel={() => setEditingFilters(false)}
            onSubmit={(change) => {
              setEditingFilters(false);
              applyFilters(change);
            }}
          />
        ) : null}
      </Dialog>

      <Dialog open={Boolean(confirming)} title={confirming ? describeAction(confirming.button, confirming.card).title : ""} labelId="assistant-confirm-title" onClose={() => setConfirming(null)}>
        {confirming ? (
          <div>
            <p className="text-sm text-slate-700">{describeAction(confirming.button, confirming.card).body}</p>
            {confirming.card?.timezone ? <p className="mt-1 text-xs text-slate-500">Times in {confirming.card.timezone}</p> : null}
            {confirming.button.reason !== "none" ? (
              <div className="mt-3">
                <label htmlFor="assistant-reason" className="text-sm font-semibold">
                  Reason {confirming.button.reason === "required" ? <span aria-hidden="true" className="text-rose-600">*</span> : <span className="font-normal text-slate-500">(optional)</span>}
                </label>
                <textarea id="assistant-reason" data-autofocus value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)}
                  aria-invalid={reasonError ? "true" : undefined} aria-describedby={reasonError ? "assistant-reason-error" : undefined}
                  className="mt-1 min-h-[80px] w-full rounded-2xl border border-slate-300 px-3 py-2 text-sm" />
                {reasonError ? <p id="assistant-reason-error" role="alert" className="text-xs font-semibold text-rose-700">{reasonError}</p> : null}
              </div>
            ) : null}
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setConfirming(null)} className="rounded-2xl border border-slate-300 px-4 py-2 text-sm font-semibold">Go back</button>
              <button type="button" onClick={confirmPending} className="rounded-2xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white">{confirming.button.label}</button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
